import {
  Box,
  Divider,
  Paper,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@crayon/design-system-react";
import { ApiPermissionOptions } from "api/client.generated";
import ProductSyncDetailsTable from "components/hoc/Sync/Product/ProductSyncDetailsTable";
import SyncStatistics from "components/hoc/Sync/SyncStatistics";
import TextHeader from "components/primitives/TextHeader";
import { useApiPermissionsContext } from "context/apiPermissionsContext";
import useProductDigest from "hooks/api/useProductDigest";
import { DateTime } from "luxon";
import { useParams } from "react-router-dom";
import SyncRouteParams from "types/sync-route-params";

interface ProductSyncHistoryDetailsRouteParams extends SyncRouteParams {
  id: string;
}

const ProductSyncHistoryDetailsPage = () => {
  const { source, target, program, id } = useParams<
    keyof ProductSyncHistoryDetailsRouteParams
  >() as ProductSyncHistoryDetailsRouteParams;

  const theme = useTheme();
  const { apiPermissions } = useApiPermissionsContext();
  const { productDigest, isProductDigestFetching } = useProductDigest(id);

  const StatsHeaderSlice = (
    <Box p={4} display="flex" justifyContent="space-between">
      <SyncStatistics
        successCount={productDigest?.stats?.syncCount ?? 0}
        errorCount={productDigest?.stats?.errorCount ?? 0}
        warningCount={productDigest?.stats?.warningCount ?? 0}
        isLoading={isProductDigestFetching}
        syncType="Product"
      />
      <Box>
        {isProductDigestFetching ? (
          <Skeleton width={180} />
        ) : (
          // prettier-ignore
          <Typography testId="Sync Date Time" variant="body1">
              {productDigest?.stats?.when
                ? DateTime.fromJSDate(new Date(productDigest!.stats!.when)).extToDateTimeString()
                : "Unknown"}
            </Typography>
        )}
      </Box>
    </Box>
  );

  const ErrorMessageSlice = productDigest?.stats.errorMessage && (
    <Box px={4} pb={2}>
      <Typography
        variant="body2"
        color={theme.palette.error.main}
        fontWeight="medium"
        testId="error-msg"
      >
        {productDigest?.stats.errorMessage}
      </Typography>
    </Box>
  );

  const ExceptionMessageSlice = productDigest?.digest.exception &&
    apiPermissions?.some((p) => p === ApiPermissionOptions.StaffAdministration) && (
      <Box px={4} pb={2}>
        <Typography
          variant="body2"
          color={theme.palette.error.main}
          fontWeight="medium"
          testId="admin-exception-msg"
        >
          {`Exception: ${productDigest?.digest.exception}`}
        </Typography>
      </Box>
    );

  return (
    <Stack spacing={2}>
      <TextHeader
        testId="page-header"
        label={`${program} Product Sync Details | ${source} > ${target}`}
      />

      <Box component={Paper}>
        {StatsHeaderSlice}
        {ErrorMessageSlice}
        {ExceptionMessageSlice}
        <Divider />
        <ProductSyncDetailsTable
          productDigest={productDigest}
          isProductDigestFetching={isProductDigestFetching}
        />
      </Box>
    </Stack>
  );
};

export default ProductSyncHistoryDetailsPage;
